import { inject, Injectable } from '@angular/core';
import { CreateServiceParams, Service, UpdateServiceParams } from '../models/service.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * The org's services-offered list (`config.manage-lists`) — design doc §7.
 * Starts empty; an org adds its own. Never hard-deleted — deactivate instead.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  /** Active, non-deleted services in display order. */
  async getActive(): Promise<Service[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM services WHERE is_active = 1 AND deleted_at IS NULL ORDER BY sort_order, label',
    );
    return rows as Service[];
  }

  /** Every non-deleted service, active or not — for admin management. */
  async getAll(): Promise<Service[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM services WHERE deleted_at IS NULL ORDER BY sort_order, label',
    );
    return rows as Service[];
  }

  createActiveQuery(): QuerySignal<Service[]> {
    return this.db.createQuery('services', () => this.getActive());
  }

  createQuery(): QuerySignal<Service[]> {
    return this.db.createQuery('services', () => this.getAll());
  }

  async create(params: CreateServiceParams): Promise<Service> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();

    await this.db.run(
      `INSERT INTO services (id, label, default_price, sort_order, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, params.label, params.default_price ?? null, params.sort_order ?? 0, userId, now, userId, now],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve service after creation');
    return created;
  }

  async update(params: UpdateServiceParams): Promise<Service> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Service '${params.id}' not found`);

    const next: Service = {
      ...existing,
      label: params.label ?? existing.label,
      default_price: params.default_price !== undefined ? params.default_price : existing.default_price,
      sort_order: params.sort_order ?? existing.sort_order,
      is_active: params.is_active ?? existing.is_active,
    };

    await this.db.run(
      `UPDATE services SET label = ?, default_price = ?, sort_order = ?, is_active = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [next.label, next.default_price, next.sort_order, next.is_active, userId, this.clockSync.now(), params.id],
    );

    return { ...next, updated_by: userId };
  }

  private async getById(id: string): Promise<Service | null> {
    const rows = await this.db.exec('SELECT * FROM services WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as Service) : null;
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} a service`);
    return userId;
  }
}
